import { Spin, Switch, Tag } from "antd";
import { ChevronDown } from "lucide-react";

import type { GetAssignableMenuGroupsParams } from "@/api/system/roles/type";
import { cn } from "@/lib/cn";
import { type EnableStatus, isEnableStatusEnabled } from "@/types";

import { usePermission } from "./hooks/usePermission";
import { PermissionTree } from "./PermissionTree";
import { collectMenuTreeNodeIds, type PermissionSelectionByApp } from "./utils";

/** 单端角色状态（needStatus 为 true 时传入） */
export interface AppRoleStatusItem {
  /** 系统端 ID */
  systemTypeId: string;
  /** 角色 ID */
  roleId: string;
  /** 启用 / 停用 */
  status: EnableStatus;
}

/** 按端分组权限选择区 props */
export interface AppPermissionSectionProps {
  /** getAssignableMenuGroups 查询参数 */
  params?: GetAssignableMenuGroupsParams;
  /** 已选权限（受控）：支持扁平 id 或按端分组（Form 回写） */
  value?: string[] | PermissionSelectionByApp[];
  /** 默认已选权限 ID（非受控） */
  defaultValue?: string[];
  /** 选中变化回调，按端返回已选 menuIds */
  onChange?: (selectionByApp: PermissionSelectionByApp[]) => void;
  /** 只读展示，不可勾选 */
  readOnly?: boolean;
  /** 是否展示各端启用/停用 Switch */
  needStatus?: boolean;
  /** 各端角色状态明细（needStatus 为 true 时传入） */
  roleStatusItems?: AppRoleStatusItem[];
  /** 单端状态切换回调（needStatus 为 true 时传入） */
  onRoleStatusChange?: (roleId: string, status: EnableStatus) => void;
}

/**
 * 按端分组的权限树折叠面板：内部拉取可选菜单分组并渲染 PermissionTree
 */
export const AppPermissionSection = ({
  params,
  value,
  defaultValue = [],
  onChange,
  readOnly = false,
  needStatus = false,
  roleStatusItems = [],
  onRoleStatusChange,
}: AppPermissionSectionProps) => {
  const { loading, groups, selectedIds, expandedIds, handleToggleExpand, handleTogglePermission } =
    usePermission({ params, value, defaultValue, onChange, readOnly });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip="加载权限中..." />
      </div>
    );
  }

  if (groups.length === 0) {
    return <div className="py-6 text-center text-sm text-slate-400">暂无可用权限</div>;
  }

  /** 按 systemTypeId 索引各端角色状态 */
  const roleStatusMap = new Map(roleStatusItems.map((item) => [item.systemTypeId, item]));

  /** 渲染单端状态 Switch */
  const renderAppStatus = (systemTypeId: string) => {
    if (!needStatus || !onRoleStatusChange) return null;
    const statusItem = roleStatusMap.get(systemTypeId);
    if (!statusItem) return null;
    const enabled = isEnableStatusEnabled(statusItem.status);
    return (
      <div
        className="flex items-center gap-1.5 shrink-0"
        onClick={(event) => event.stopPropagation()}
      >
        <Switch
          size="small"
          checked={enabled}
          disabled={!onRoleStatusChange}
          onChange={(checked) =>
            onRoleStatusChange?.(statusItem.roleId, checked ? "ENABLED" : "DISABLED")
          }
        />
        <span className={cn("text-xs", enabled ? "text-green-600" : "text-slate-400")}>
          {enabled ? "启用" : "停用"}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {groups.map((appConfig) => {
        /** 系统端 ID */
        const appKey = appConfig.systemTypeId;
        /** 系统端是否展开 */
        const isExpanded = expandedIds.has(appKey);
        /** 系统端菜单树节点列表 */
        const treeNodes = appConfig.menuTree ?? [];
        /** 系统端菜单节点 ID 列表 */
        const appMenuIds = collectMenuTreeNodeIds(treeNodes);
        /** 系统端已选菜单数量 */
        const appSelectedCount = selectedIds.filter((id) => appMenuIds.includes(id)).length;
        return (
          <div key={appKey} className="border rounded-lg overflow-hidden bg-white">
            <div
              className="flex items-center justify-between px-4 py-2.5 bg-slate-50/80 cursor-pointer hover:bg-slate-100/80 transition-colors"
              onClick={() => handleToggleExpand(appKey, true)}
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-slate-400 transition-transform",
                    !isExpanded && "-rotate-90",
                  )}
                />
                <span className="text-sm font-medium">{appConfig.systemTypeName}</span>
                <span className="text-xs text-slate-400">({appConfig.systemTypeCode})</span>
                <Tag bordered={false} className="text-[10px] m-0">
                  {treeNodes.length} 项权限
                </Tag>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-slate-400">
                  已选 {appSelectedCount}/{appMenuIds.length}
                </span>
                {renderAppStatus(appKey)}
              </div>
            </div>
            {isExpanded && (
              <div className="p-2 border-t">
                <PermissionTree
                  readOnly={readOnly}
                  nodes={treeNodes}
                  selectedPermissions={selectedIds}
                  expandedIds={expandedIds}
                  onToggleExpand={handleToggleExpand}
                  onToggle={handleTogglePermission}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
